import { FIBRE_ERROR, FIBRE_LOADING } from './fibre';
import { dl, f0, f1, MUTED, NEUTRAL, pc } from './format';
import { WATER_ERROR, WATER_LOADING } from './water';

// The five score cards. Points follow the design's `pts`: symptoms score
// (5 − weekly average)/5 × 30, the others value/100 × weight; a metric with no
// data scores 0 — except fibre with no target set, or water logged without a
// plan, which show "–".

export const CARD_METRICS = [
  { id: 'sym', tab: 'sym', name: 'Symptoms', color: '#B5654A', w: 30, val: (x) => x.sym.avg, fmt: f1, unit: '/5 avg', dir: -1 },
  { id: 'stool', tab: 'bowel', name: 'Stool consistency', color: '#8A6A58', w: 20, val: (x) => x.bowel.normal, fmt: pc, unit: 'Bristol 3–4', dir: 1 },
  { id: 'comfort', tab: 'bowel', name: 'Bowel comfort', color: '#A3826C', w: 15, val: (x) => x.bowel.comfort, fmt: pc, unit: '', dir: 1 },
  { id: 'fibre', tab: 'nut', nut: 'fib', name: 'Fibre adherence', color: '#5E8C5A', w: 10, val: (x) => x.fibre.pct, fmt: pc, unit: 'days on target', dir: 1 },
  { id: 'hyd', tab: 'nut', nut: 'wat', name: 'Hydration adherence', color: '#3E8FB0', w: 10, val: (x) => x.water.pct, fmt: pc, unit: 'of target', dir: 1 },
];

/** Why a fibre or water value can't be shown for a week: 'loading', 'error', 'none' (no target), or 'ready'. */
function sourceState(ctx, m, x) {
  if (m.id === 'fibre') {
    if (x.fibre.status !== 'ready') return x.fibre.status;
    return ctx.fibreTarget ? 'ready' : 'none';
  }
  if (m.id === 'hyd') {
    if (x.water.status !== 'ready') return x.water.status;
    return x.water.n && !x.water.hasPlan ? 'none' : 'ready';
  }
  return 'ready';
}

const BLANK = { error: '–', loading: '…', none: '–' };
const NOTES = {
  fibre: { error: FIBRE_ERROR, loading: FIBRE_LOADING, none: 'No fibre target set' },
  hyd: { error: WATER_ERROR, loading: WATER_LOADING, none: 'No water target set' },
};

/** Points for one metric in a week; null when it isn't scored (no target, or not loaded). */
export function points(ctx, m, x) {
  if (sourceState(ctx, m, x) !== 'ready') return null;
  const v = m.val(x);
  if (v == null) return 0;
  return m.id === 'sym' ? ((5 - v) / 5) * m.w : (v / 100) * m.w;
}

/**
 * @param {ReturnType<import('./compare').compareWith>} cmp
 * @param {string} tab - Active Daily Log tab.
 * @param {'fib'|'wat'} [nut] - The measure shown on the Nutrition tab.
 */
export function buildCards(ctx, cmp, tab, nut = 'fib') {
  return CARD_METRICS.map((m) => {
    const c = m.val(cmp.cur);
    const r = m.val(cmp.ref);
    const p = points(ctx, m, cmp.cur);
    // Fibre and water: the selected week's value, then the compared week's, may
    // still be loading or have failed; either says so rather than "no data".
    const cur = sourceState(ctx, m, cmp.cur);
    const ref = cmp.same ? 'ready' : sourceState(ctx, m, cmp.ref);
    const blank = BLANK[cur];
    const blankNote = NOTES[m.id]?.[cur !== 'ready' ? cur : ref === 'none' ? 'ready' : ref];
    let delta;
    let dfg;
    if (blankNote) {
      delta = blankNote;
      dfg = NEUTRAL;
    } else if (cmp.same) {
      delta = 'First logged week';
      dfg = MUTED;
    } else if (c == null || r == null) {
      delta = r == null ? `No data ${cmp.word === 'last week' ? 'last week' : 'in first logged week'}` : 'Not enough data';
      dfg = NEUTRAL;
    } else {
      const isSym = m.id === 'sym';
      const [d, f] = dl(isSym ? c - r : Math.round(c - r), m.dir, isSym ? f1 : f0);
      delta = d === 'No change' ? `No change · ${m.fmt(r)}` : `${d}${isSym ? '' : '%'} · from ${m.fmt(r)}`;
      dfg = f;
    }
    return {
      id: m.id,
      tab: m.tab,
      nut: m.nut,
      name: m.name,
      color: m.color,
      value: blank || m.fmt(c),
      unit: blank ? '' : m.unit,
      delta,
      dfg,
      ptsTxt: `${p == null ? '–' : f1(p)} / ${m.w}`,
      active: m.tab === tab && (!m.nut || m.nut === nut),
    };
  });
}
